import React from "react";
import { AlertTriangle, CheckCircle2, Copy, X } from "lucide-react";
import { Modal, MODAL_HEADER } from "../shared/Modal";
import { useAppContext } from "../../AppContext";
import { PaymentProofForm } from "./PaymentProofForm";
import { reviewReasonClientMessage, isUnregisteredBank } from "../../lib/reviewReasons";

/* -----------------------------------------------------------------------------
 * PaymentProofModal — the "Confirmación de Pago" modal shown after checkout.
 * Users pick a payment method (Pago Móvil / Transferencia / USD platform),
 * enter the amount paid, upload the proof image, and confirm. The AI reads
 * the reference from the image; a manual fallback appears iff OCR fails.
 * Extracted verbatim from AppModals.jsx.
 * --------------------------------------------------------------------------- */
export function PaymentProofModal() {
  const ctx = useAppContext();
  const {
    showPaymentModal, setShowPaymentModal, submitting,
    cart, cartTotalBs, cartTotalUsd, currency,
    paymentMethods, selectedPmId, setSelectedPmId,
    checkoutPmTab, setCheckoutPmTab,
    formatBs, formatUsd, formatItemMoney, copyToClipboard,
    // Payment result mini-modal shown right after this modal closes
    paymentResultModal, setPaymentResultModal, lastOrderResult,
    setClientTab,
  } = ctx;

  // No early return — both the payment modal (guarded by showPaymentModal &&
  // cart.length > 0) and the payment-result mini-modal (guarded by
  // paymentResultModal && lastOrderResult) may need to render independently.

  return (
    <>
      {showPaymentModal && cart.length > 0 && (
        <Modal onClose={() => !submitting && setShowPaymentModal(false)} testId="payment-modal" size="lg">
          <div className={MODAL_HEADER}>
            <div>
              <span className="text-xs font-mono text-purple-400">CONFIRMACIÓN DE PAGO</span>
              <h3 className="text-xl font-extrabold text-white">{cart.length} producto{cart.length !== 1 ? "s" : ""} en el carrito</h3>
            </div>
            <button data-testid="payment-proof-modal-button-1" onClick={() => !submitting && setShowPaymentModal(false)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full disabled:opacity-40" disabled={submitting}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cart summary */}
          <div className="p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-purple-950/30 border border-purple-500/30 space-y-1.5 sm:space-y-2 max-h-40 sm:max-h-52 overflow-y-auto">
            {cart?.map((it) => {
              const m = formatItemMoney(it);
              return (
                <div key={it.tempId} className="flex justify-between text-[11px] sm:text-xs">
                  <span className="text-zinc-300 truncate mr-2">{it.gameName} · {it.packageName}</span>
                  <span className="font-bold text-white font-mono flex-shrink-0">{m.primary}</span>
                </div>
              );
            })}
            <div className="flex flex-col border-t border-purple-500/20 pt-1.5 sm:pt-2 mt-1.5 sm:mt-2">
              <div className="flex justify-between items-baseline">
                <span className="text-purple-300 font-bold text-xs sm:text-base">Total a Pagar:</span>
                <span className="font-extrabold text-cyan-400 font-mono text-sm sm:text-lg" data-testid="modal-cart-total">
                  {currency === "USD" ? formatUsd(cartTotalUsd) : formatBs(cartTotalBs)}
                </span>
              </div>
              <div className="flex justify-end">
                <span className="text-[10px] sm:text-[11px] text-zinc-500 font-mono" data-testid="modal-cart-total-alt">
                  ≈ {currency === "USD" ? formatBs(cartTotalBs) : formatUsd(cartTotalUsd)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment method category tabs + filtered selector */}
          <div className="space-y-2 sm:space-y-3">
            <label className="block text-[11px] sm:text-xs font-mono text-zinc-400">Método de Pago Destino:</label>
            {(() => {
              const active = paymentMethods?.filter(pm => pm.active !== false);
              const groups = [
                { id: "PAGO_MOVIL", label: "Pago Móvil", color: "cyan", count: active?.filter(pm => pm.type === "PAGO_MOVIL").length },
                { id: "TRANSFERENCIA", label: "Transferencia", color: "purple", count: active?.filter(pm => pm.type === "TRANSFERENCIA").length },
                { id: "USD_PLATFORM", label: "Pago en $", color: "green", count: active?.filter(pm => pm.type === "USD_PLATFORM").length },
              ].filter(g => g.count > 0);
              const currentTab = groups?.some(g => g.id === checkoutPmTab) ? checkoutPmTab : (groups[0]?.id || "PAGO_MOVIL");
              const filtered = active?.filter(pm => pm.type === currentTab);
              // Auto-select the first PM of the current tab whenever the tab changes / list changes
              const ids = filtered?.map(f => f.id);
              if (!ids.includes(selectedPmId) && ids.length > 0) {
                setTimeout(() => setSelectedPmId(ids[0]), 0);
              }
              return (
                <>
                  <div className="flex flex-wrap gap-2" data-testid="checkout-pm-tabs">
                    {groups?.map(g => {
                      const isActive = currentTab === g.id;
                      const palette = isActive
                        ? { cyan: "bg-cyan-500/25 border-cyan-400 text-cyan-100", purple: "bg-purple-500/25 border-purple-400 text-purple-100", green: "bg-green-500/25 border-green-400 text-green-100" }[g.color]
                        : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-white";
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => { setCheckoutPmTab(g.id); }}
                          data-testid={`checkout-pm-tab-${g.id}`}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border text-[10px] sm:text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 sm:gap-2 ${palette}`}
                        >
                          <span>{g.label}</span>
                          <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-black/40 text-[9px] sm:text-[10px] font-mono">{g.count}</span>
                        </button>
                      );
                    })}
                  </div>
                  {filtered.length > 1 && (
                    <select value={selectedPmId} onChange={(e) => setSelectedPmId(e.target.value)} data-testid="payment-method-select"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-2.5 text-white text-xs font-mono">
                      {filtered?.map(pm => (
                        <option key={pm.id} value={pm.id}>
                          {pm.type === "USD_PLATFORM"
                            ? `${pm.platformName} · ${pm.accountIdentifier}`
                            : pm.type === "TRANSFERENCIA"
                              ? `${pm.bankName} · ${pm.accountNumber}`
                              : `${pm.bankName} · ${pm.phone}`}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              );
            })()}
            {(() => {
              const pm = paymentMethods?.find(x => x.id === selectedPmId);
              if (!pm) return <p className="text-xs text-zinc-500 italic">Sin métodos configurados para esta categoría.</p>;
              let rows = [];
              if (pm.type === "USD_PLATFORM") {
                rows = [
                  { label: "Plataforma", value: pm.platformName },
                  { label: "Correo/ID", value: pm.accountIdentifier },
                  ...(pm.accountHolder ? [{ label: "Titular", value: pm.accountHolder }] : []),
                ];
              } else if (pm.type === "TRANSFERENCIA") {
                rows = [
                  { label: "Banco", value: pm.bankName },
                  { label: "Titular", value: pm.accountHolder },
                  { label: "Cédula/RIF", value: pm.documentId },
                  { label: "Cuenta", value: pm.accountNumber },
                ];
              } else {
                rows = [
                  { label: "Banco", value: `${pm.bankName}${pm.bankCode ? " (" + pm.bankCode + ")" : ""}` },
                  { label: "Titular", value: pm.accountHolder },
                  { label: "Cédula/RIF", value: pm.documentId },
                  { label: "Teléfono", value: pm.phone },
                ];
              }
              const allText = rows?.map(r => `${r.label}: ${r.value}`).join("\n");
              return (
                <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-zinc-950 border border-cyan-500/30 text-[10px] sm:text-[11px] font-mono space-y-1.5 sm:space-y-2 text-zinc-300">
                  {rows?.map((r) => (
                    <div key={r.label} className="flex items-center justify-between gap-2">
                      <p className="min-w-0 flex-1">
                        <span className="text-zinc-500">{r.label}:</span>{" "}
                        <strong className="text-white break-all">{r.value}</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(String(r.value), `${r.label} copiado`)}
                        data-testid={`copy-pm-${r.label.toLowerCase().replace(/[^a-z]/g,'')}`}
                        title={`Copiar ${r.label}`}
                        className="flex-shrink-0 bg-cyan-800/50 hover:bg-cyan-700 text-cyan-100 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg text-[9px] sm:text-[10px] font-bold flex items-center gap-1 border border-cyan-500/30"
                      >
                        <Copy className="w-3 h-3" /> <span className="hidden xs:inline">Copiar</span>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => copyToClipboard(allText, "Todos los datos copiados")}
                    data-testid="copy-pm-all"
                    className="w-full mt-1.5 sm:mt-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-95 text-white font-bold text-[10px] sm:text-xs py-1.5 sm:py-2 rounded-md sm:rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    Copiar todos los datos
                  </button>
                </div>
              );
            })()}
          </div>


          <PaymentProofForm />
        </Modal>
      )}

      {/* ============ ORDER RESULT MODAL ============ */}
      {paymentResultModal && lastOrderResult && (
        <Modal onClose={() => setPaymentResultModal(false)} size="sm">
          <div className="text-center space-y-4">
            {lastOrderResult.verification_status === "NEEDS_REVIEW" ? (
              <>
                <div className="w-16 h-16 rounded-full bg-amber-950/60 border-2 border-amber-500 flex items-center justify-center mx-auto animate-pulse">
                  <AlertTriangle className="w-8 h-8 text-amber-300" />
                </div>
                <h3 className="text-2xl font-extrabold text-white" data-testid="result-needs-review-title">Pedido en Revisión</h3>
                {lastOrderResult.cart_size > 1 ? (
                  <p className="text-xs font-mono text-zinc-400" data-testid="result-order-id">{lastOrderResult.cart_size} productos registrados</p>
                ) : (
                  <p className="text-xs font-mono text-zinc-400" data-testid="result-order-id">ID: {lastOrderResult.order.id}</p>
                )}
                <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-500/40 text-amber-200 text-xs leading-relaxed" data-testid="result-needs-review-message">
                  {reviewReasonClientMessage(lastOrderResult.review_reason)}
                </div>
                {isUnregisteredBank(lastOrderResult.review_reason) && (
                  <p className="text-[11px] font-mono text-amber-300/80 px-2" data-testid="result-unregistered-bank-note">
                    Banco no reconocido automáticamente · verificación manual en curso
                  </p>
                )}
                <button onClick={() => { setPaymentResultModal(false); setClientTab("orders"); }}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-3 rounded-xl text-xs" data-testid="view-my-orders-btn">
                  Ver Mis Pedidos
                </button>
              </>
            ) : lastOrderResult.is_partial ? (
              <>
                <div className="w-16 h-16 rounded-full bg-orange-950/60 border-2 border-orange-500 flex items-center justify-center mx-auto">
                  <AlertTriangle className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-2xl font-extrabold text-white" data-testid="result-partial-payment-title">Pago Parcial Detectado</h3>
                <p className="text-xs font-mono text-zinc-400" data-testid="result-order-id">ID: {lastOrderResult.order.id}</p>
                <div className="p-3 rounded-xl bg-orange-950/30 border border-orange-500/40 text-orange-200 text-xs" data-testid="result-partial-payment-shortage">
                  Falta un total de <strong>Bs {Math.round(lastOrderResult.shortage_bs)}</strong> para completar tu pedido.
                </div>
                <div className="p-3 rounded-xl bg-zinc-950 border border-zinc-800 text-xs text-zinc-300">
                  Ve a <strong>Historial</strong> y presiona &quot;Completar Pago&quot; para adjuntar una nueva referencia con el monto faltante.
                </div>
                <button onClick={() => { setPaymentResultModal(false); setClientTab("orders"); }}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 rounded-xl text-xs" data-testid="view-my-orders-btn">
                  Ver Mis Pedidos
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-green-950/60 border-2 border-green-500 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-extrabold text-white">¡Pedido{lastOrderResult.cart_size > 1 ? "s" : ""} Realizado{lastOrderResult.cart_size > 1 ? "s" : ""}!</h3>
                {lastOrderResult.cart_size > 1 ? (
                  <p className="text-xs font-mono text-zinc-400" data-testid="result-order-id">{lastOrderResult.cart_size} productos registrados</p>
                ) : (
                  <p className="text-xs font-mono text-zinc-400" data-testid="result-order-id">ID: {lastOrderResult.order.id}</p>
                )}
                {lastOrderResult.surplus_bs > 0 && (
                  <div className="p-3 rounded-xl bg-green-950/30 border border-green-500/40 text-green-300 text-xs">
                    {lastOrderResult.surplus_credited
                      ? <>💰 Excedente detectado: <strong>Bs {Math.round(lastOrderResult.surplus_bs)}</strong> acreditado como saldo a favor.</>
                      : <>💰 Excedente detectado: <strong>Bs {Math.round(lastOrderResult.surplus_bs)}</strong>. Se acreditará a tu saldo a favor cuando un agente valide el pago.</>}
                  </div>
                )}
                <div className="p-3 rounded-xl bg-zinc-950 border border-zinc-800 text-xs text-zinc-300">
                  Tu recarga será procesada en <strong>5-15 min</strong>.
                </div>
                <button onClick={() => { setPaymentResultModal(false); setClientTab("orders"); }}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-xl text-xs" data-testid="view-my-orders-btn">
                  Ver Mis Pedidos
                </button>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
